// src/pages/GamificationPage.tsx
import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, Tabs, List, Avatar, message, Modal, Tag, Empty } from 'antd';
import { TrophyOutlined, GiftOutlined, HistoryOutlined, CheckCircleOutlined, ShoppingCartOutlined, StarOutlined } from '@ant-design/icons';
import { gamificationAPI } from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface Achievement {
  id: number | null;
  achievement_type: string;
  achievement_name: string;
  achievement_description: string | null;
  butterfly_reward: number;
  unlocked_at: string | null;
  is_unlocked: boolean;
  is_recurring?: boolean;
}

interface Reward {
  id: number;
  name: string;
  description: string | null;
  butterfly_cost: number;
  reward_type: string;
  reward_data: any;
  stock_quantity: number | null;
  image_url: string | null;
}

interface Transaction {
  id: number;
  amount: number;
  transaction_type: string;
  description: string | null;
  created_at: string | null;
}

export const GamificationPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [myRewards, setMyRewards] = useState<any[]>([]);
  const [purchasing, setPurchasing] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, achievementsRes, rewardsRes, transactionsRes, myRewardsRes] = await Promise.all([
        gamificationAPI.getBalance(),
        gamificationAPI.getAchievements(),
        gamificationAPI.getRewards(),
        gamificationAPI.getTransactions(20),
        gamificationAPI.getMyRewards()
      ]);

      console.log('Achievements response:', achievementsRes.data);
      
      setBalance((balanceRes.data as any).butterfly_balance || 0);
      const achievementsData = (achievementsRes.data as any).achievements || [];
      console.log('Parsed achievements:', achievementsData);
      setAchievements(achievementsData);
      setRewards((rewardsRes.data as any).rewards || []);
      setTransactions((transactionsRes.data as any).transactions || []);
      setMyRewards((myRewardsRes.data as any).rewards || []);
    } catch (error: any) {
      console.error('Error loading gamification data:', error);
      console.error('Error details:', error.response?.data);
      message.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (reward: Reward) => {
    if (balance < reward.butterfly_cost) {
      message.warning('Недостаточно бабочек для покупки');
      return;
    }

    Modal.confirm({
      title: 'Подтверждение покупки',
      content: `Вы уверены, что хотите купить "${reward.name}" за ${reward.butterfly_cost} бабочек?`,
      okText: 'Купить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          setPurchasing(reward.id);
          await gamificationAPI.purchaseReward(reward.id);
          message.success('Награда успешно приобретена!');
          await loadData(); // Reload data
        } catch (error: any) {
          console.error('Purchase error:', error);
          message.error(error.response?.data?.detail || 'Ошибка при покупке');
        } finally {
          setPurchasing(null);
        }
      }
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#F0F9F4', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header with Balance */}
        <Card
          style={{
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #7CB342 0%, #F4A261 100%)',
            border: 'none',
            borderRadius: '12px'
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Title level={2} style={{ color: '#fff', margin: 0 }}>
                🦋 Геймификация
              </Title>
              <Text style={{ color: '#fff', fontSize: '16px', opacity: 0.9 }}>
                Зарабатывайте бабочки и получайте награды!
              </Text>
            </Col>
            <Col>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '48px', color: '#fff', fontWeight: 'bold', lineHeight: 1 }}>
                  {balance}
                </div>
                <Text style={{ color: '#fff', fontSize: '18px' }}>🦋 бабочек</Text>
              </div>
            </Col>
          </Row>
        </Card>

        <Tabs defaultActiveKey="achievements" size="large">
          <TabPane
            tab={
              <span>
                <TrophyOutlined style={{ marginRight: 8 }} />
                Достижения
              </span>
            }
            key="achievements"
          >
            <Card loading={loading}>
              {!loading && achievements.length === 0 ? (
                <Empty
                  description="У вас пока нет достижений. Выполняйте действия в дневниках, чтобы заработать достижения!"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Row gutter={[16, 16]}>
                  {achievements.map((achievement) => {
                    const isUnlocked = achievement.is_unlocked || false;
                    return (
                      <Col xs={24} sm={12} lg={8} key={achievement.achievement_type}>
                        <Card
                          hoverable
                          style={{
                            height: '100%',
                            borderRadius: '12px',
                            border: isUnlocked ? '2px solid #7CB342' : '2px solid #d9d9d9',
                            backgroundColor: isUnlocked ? '#f6ffed' : '#fafafa',
                            opacity: isUnlocked ? 1 : 0.8
                          }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <TrophyOutlined
                                style={{
                                  fontSize: '48px',
                                  color: isUnlocked ? '#7CB342' : '#d9d9d9',
                                  marginBottom: '12px'
                                }}
                              />
                              {isUnlocked && (
                                <CheckCircleOutlined
                                  style={{
                                    position: 'absolute',
                                    top: '-4px',
                                    right: '-4px',
                                    fontSize: '20px',
                                    color: '#7CB342',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%'
                                  }}
                                />
                              )}
                            </div>
                            <Title level={4} style={{ marginBottom: '8px', color: isUnlocked ? '#7CB342' : '#595959' }}>
                              {achievement.achievement_name}
                            </Title>
                            {achievement.achievement_description && (
                              <Paragraph style={{ color: '#666', marginBottom: '12px' }}>
                                {achievement.achievement_description}
                              </Paragraph>
                            )}
                            <div style={{ marginTop: '12px' }}>
                              <Tag color={isUnlocked ? "gold" : "default"} style={{ fontSize: '14px', padding: '4px 12px' }}>
                                +{achievement.butterfly_reward} 🦋
                              </Tag>
                              {achievement.is_recurring && (
                                <Tag color="blue" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                  Регулярное
                                </Tag>
                              )}
                            </div>
                            {isUnlocked && achievement.unlocked_at && (
                              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                Получено: {formatDate(achievement.unlocked_at)}
                              </Text>
                            )}
                            {!isUnlocked && (
                              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px', fontStyle: 'italic' }}>
                                Не выполнено
                              </Text>
                            )}
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <GiftOutlined style={{ marginRight: 8 }} />
                Магазин наград
              </span>
            }
            key="rewards"
          >
            <Card loading={loading}>
              {rewards.length === 0 ? (
                <Empty
                  description="Скоро здесь будут награды"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Row gutter={[16, 16]}>
                  {rewards.map((reward) => {
                    const canAfford = balance >= reward.butterfly_cost;
                    const isOutOfStock = reward.stock_quantity !== null && reward.stock_quantity <= 0;

                    return (
                      <Col xs={24} sm={12} lg={8} key={reward.id}>
                        <Card
                          hoverable
                          style={{
                            height: '100%',
                            borderRadius: '12px',
                            border: canAfford && !isOutOfStock ? '2px solid #7CB342' : '1px solid #d9d9d9',
                            opacity: isOutOfStock ? 0.6 : 1
                          }}
                          actions={[
                            <Button
                              type="primary"
                              icon={<ShoppingCartOutlined />}
                              onClick={() => handlePurchase(reward)}
                              disabled={!canAfford || isOutOfStock || purchasing === reward.id}
                              loading={purchasing === reward.id}
                              style={{
                                width: '100%',
                                backgroundColor: canAfford && !isOutOfStock ? '#7CB342' : '#d9d9d9',
                                borderColor: canAfford && !isOutOfStock ? '#7CB342' : '#d9d9d9'
                              }}
                            >
                              {isOutOfStock
                                ? 'Нет в наличии'
                                : !canAfford
                                ? `Нужно ${reward.butterfly_cost} 🦋`
                                : `Купить за ${reward.butterfly_cost} 🦋`}
                            </Button>
                          ]}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <GiftOutlined
                              style={{
                                fontSize: '48px',
                                color: '#7CB342',
                                marginBottom: '12px'
                              }}
                            />
                            <Title level={4} style={{ marginBottom: '8px' }}>
                              {reward.name}
                            </Title>
                            {reward.description && (
                              <Paragraph style={{ color: '#666', marginBottom: '12px' }}>
                                {reward.description}
                              </Paragraph>
                            )}
                            <div style={{ marginTop: '12px' }}>
                              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                {reward.butterfly_cost} 🦋
                              </Tag>
                              {reward.stock_quantity !== null && (
                                <Tag color="orange" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                  Осталось: {reward.stock_quantity}
                                </Tag>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Мои награды
              </span>
            }
            key="my-rewards"
          >
            <Card loading={loading}>
              {myRewards.length === 0 ? (
                <Empty
                  description="У вас пока нет приобретенных наград"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={myRewards}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            icon={<GiftOutlined />}
                            style={{ backgroundColor: item.is_used ? '#d9d9d9' : '#7CB342' }}
                          />
                        }
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{item.reward.name}</span>
                            {item.is_used ? (
                              <Tag color="default">Использовано</Tag>
                            ) : (
                              <Tag color="success">Активно</Tag>
                            )}
                          </div>
                        }
                        description={
                          <div>
                            <div>{item.reward.description}</div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Приобретено: {formatDate(item.purchased_at)}
                            </Text>
                            {item.reward.reward_data?.code && (
                              <div style={{ marginTop: '8px' }}>
                                <Text strong>Промокод: </Text>
                                <Text code style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                  {item.reward.reward_data.code}
                                </Text>
                              </div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <HistoryOutlined style={{ marginRight: 8 }} />
                История транзакций
              </span>
            }
            key="transactions"
          >
            <Card loading={loading}>
              {transactions.length === 0 ? (
                <Empty
                  description="У вас пока нет транзакций"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={transactions}
                  renderItem={(transaction) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: transaction.amount > 0 ? '#7CB342' : '#ff4d4f'
                            }}
                            icon={transaction.amount > 0 ? <StarOutlined /> : <ShoppingCartOutlined />}
                          />
                        }
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{transaction.description || 'Транзакция'}</span>
                            <Tag color={transaction.amount > 0 ? 'success' : 'error'}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount} 🦋
                            </Tag>
                          </div>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatDate(transaction.created_at)}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};
